import { useEffect } from "react";
import { isMobileApp } from "../utils/isMobileApp";
import { runFaceIdGate } from "../utils/faceIdGate";
import { tryRefreshSession, isCrossSiteToBackend } from "../utils/loginUtils";
import { FrontendBrandConfig } from "../utils/brandConfig";

interface Props {
  brand: FrontendBrandConfig;
  loadingText?: string;
}

// Every brand's door: restore the cookie session silently when one exists, else hand over to Auth0 (SSO decides).
export default function LoginGate({ brand, loadingText = "Connecting..." }: Props) {
  useEffect(() => {
    let cancelled = false;

    (async () => {
      // A branded custom domain is cross-site to the backend, so no cookie can be restored there: hop to the shared frontend first.
      const sharedFrontend = process.env.REACT_APP_FRONTEND_URL;
      if (isCrossSiteToBackend() && sharedFrontend) {
        window.location.href = `${sharedFrontend}${brand.loginPath}`;
        return;
      }

      const mobileMode =
        isMobileApp() ||
        window.navigator.userAgent.includes("iPhone") ||
        window.navigator.userAgent.includes("iPad");

      const gate = await runFaceIdGate();
      if (cancelled) return;

      let forceReAuth = false;
      try {
        forceReAuth = sessionStorage.getItem("asksmart.forceReAuth") === "1";
        if (forceReAuth) sessionStorage.removeItem("asksmart.forceReAuth");
      } catch (_) {}

      // The web always tries; on mobile the biometric gate decides.
      const mayRefresh =
        !forceReAuth && (gate.reason === "not-mobile" || gate.mode === "authenticated");

      if (mayRefresh) {
        const restored = await tryRefreshSession();
        if (cancelled) return;
        // A session of another brand cannot serve this door; the default door routes any session by /session.
        if (restored.ok && (brand.isDefault || restored.brand === brand.brand)) {
          window.location.href = brand.successPath;
          return;
        }
      }

      const params = new URLSearchParams();
      if (mobileMode) params.set("platform", "mobile");
      if (forceReAuth) params.set("force", "true");
      const qs = params.toString();
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}${brand.authEndpoint}${qs ? `?${qs}` : ""}`;
    })();

    return () => {
      cancelled = true;
    };
  }, [brand]);

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "18px",
        fontWeight: "500",
      }}
    >
      {loadingText}
    </div>
  );
}
